import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { AdminFormRow } from "./types";

export type RemoveFieldInput = {
  formId: string;
  fieldId: string;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// DELETE /api/admin/forms/:formId/fields/:fieldId
//
// Optimistic — drops the field from its section in detail cache, rolls back
// on error.
export function useRemoveField() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiClientError, RemoveFieldInput, Context>({
    mutationFn: ({ formId, fieldId }) =>
      apiFetch<void>(`/admin/forms/${formId}/fields/${fieldId}`, {
        method: "DELETE",
      }),
    onMutate: async ({ formId, fieldId }) => {
      const detailKey = queryKeys.adminForms.detail(formId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          definition: {
            ...old.definition,
            sections: old.definition.sections.map((s) => ({
              ...s,
              fields: s.fields.filter((f) => f.id !== fieldId),
            })),
          },
        };
      });
      return { previousDetail };
    },
    onError: (err, { formId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.adminForms.detail(formId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to remove field");
    },
    onSettled: (_data, _err, { formId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminForms.detail(formId),
      });
    },
  });
}
